import {
  captureAdminSession, clearAdminSession, commitAdminRefresh,
  requireCurrentAdminSession, isCurrentAdminSession, isAdminTokenValid,
  waitForAdminRefreshRetry, type AdminSessionOwner,
  AdminAuthBusyError,
} from './adminAuthSession'
export { AdminAuthBusyError } from './adminAuthSession'
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { resolveApiConfig, getApiClientHeader } from './apiConfig'
import { sanitizeErrorForLogging } from '@/utils/logging'
import {
  buildUnauthorizedRefreshError,
  parseRefreshSignal,
  type RefreshSignal,
} from '@/utils/refreshSignal'

const { apiBaseUrl } = resolveApiConfig()
const REFRESH_TIMEOUT_MS = 15000
const REFRESH_LOCK_TTL_MS = 35000
const AUTH_WRITE_LOCK_WAIT_MS = REFRESH_LOCK_TTL_MS
const AUTH_WRITE_LOCK_POLL_MS = 100
const REFRESH_LOCK_KEY = 'cms_admin_refresh_lock'
const REFRESH_SIGNAL_KEY = 'cms_admin_refresh_signal'
const REFRESH_CHANNEL_NAME = 'cms-admin-refresh'
const REFRESH_STORAGE_PROBE_KEY = 'cms_admin_refresh_probe'
const ADMIN_TAB_ID =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `admin-${Date.now()}-${Math.random().toString(16).slice(2)}`

type RefreshResponse = {
  token: string
  expiresAt: string
  refreshToken?: string
  sessionId: string
  user?: any
}

type AxiosRequestConfigWithRetry = AxiosRequestConfig & {
  __adminAccessToken?: string | null
  __adminSessionOwner?: AdminSessionOwner
  _retry?: boolean
  __preserveAppClientHeader?: boolean
}

const resolveExplicitAppClientHeader = (headers: unknown): string | null => {
  if (!headers || typeof headers !== 'object') {
    return null
  }

  const headerBag = headers as {
    get?: (name: string) => string | null | undefined
    ['X-App-Client']?: unknown
    ['x-app-client']?: unknown
  }

  const fromGetter =
    typeof headerBag.get === 'function' ? headerBag.get('X-App-Client') : undefined
  const directValue =
    typeof fromGetter === 'string' && fromGetter.trim().length > 0
      ? fromGetter
      : (headerBag['X-App-Client'] ?? headerBag['x-app-client'])

  if (typeof directValue !== 'string') {
    return null
  }

  const normalized = directValue.trim()
  return normalized.length > 0 ? normalized : null
}

const adminRefreshChannel =
  typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel(REFRESH_CHANNEL_NAME)
    : null

const hasLocalStorage =
  typeof window !== 'undefined' &&
  (() => {
    try {
      localStorage.setItem(REFRESH_STORAGE_PROBE_KEY, '1')
      localStorage.removeItem(REFRESH_STORAGE_PROBE_KEY)
      return true
    } catch {
      return false
    }
  })()

let latestRefreshSignal: RefreshSignal | null = null

const readRefreshSignal = (): RefreshSignal | null => {
  if (typeof window === 'undefined') {
    return null
  }
  if (!hasLocalStorage) {
    return latestRefreshSignal
  }
  const raw = localStorage.getItem(REFRESH_SIGNAL_KEY)
  if (!raw) {
    return null
  }
  try {
    return parseRefreshSignal(JSON.parse(raw))
  } catch {
    return null
  }
}

const broadcastRefreshSignal = (signal: RefreshSignal): void => {
  if (typeof window === 'undefined') {
    return
  }
  latestRefreshSignal = signal
  try {
    if (hasLocalStorage) {
      localStorage.setItem(REFRESH_SIGNAL_KEY, JSON.stringify(signal))
    }
  } catch {
    // Ignore storage write failures
  }
  if (adminRefreshChannel) {
    adminRefreshChannel.postMessage(signal)
  }
}

const readRefreshLock = (): { owner: string; expiresAt: number } | null => {
  if (typeof window === 'undefined') {
    return null
  }
  if (!hasLocalStorage) {
    return null
  }
  const raw = localStorage.getItem(REFRESH_LOCK_KEY)
  if (!raw) {
    return null
  }
  try {
    const parsed = JSON.parse(raw) as { owner?: string; expiresAt?: number }
    if (typeof parsed.owner !== 'string' || typeof parsed.expiresAt !== 'number') {
      return null
    }
    return { owner: parsed.owner, expiresAt: parsed.expiresAt }
  } catch {
    return null
  }
}

const acquireRefreshLock = (): boolean => {
  if (typeof window === 'undefined') {
    return true
  }
  if (!hasLocalStorage) {
    return false
  }
  const now = Date.now()
  const current = readRefreshLock()
  if (!current || current.expiresAt <= now) {
    const next = { owner: ADMIN_TAB_ID, expiresAt: now + REFRESH_LOCK_TTL_MS }
    try {
      localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify(next))
    } catch {
      return false
    }
    return readRefreshLock()?.owner === ADMIN_TAB_ID
  }
  return current.owner === ADMIN_TAB_ID
}

const releaseRefreshLock = (): void => {
  if (typeof window === 'undefined') {
    return
  }
  if (!hasLocalStorage) {
    return
  }
  const current = readRefreshLock()
  if (!current || current.owner !== ADMIN_TAB_ID) {
    return
  }
  try {
    localStorage.removeItem(REFRESH_LOCK_KEY)
  } catch {
    // Ignore storage errors
  }
}

const waitForRefreshLock = async (
  owner: AdminSessionOwner, deadline = Date.now() + AUTH_WRITE_LOCK_WAIT_MS,
): Promise<boolean> => {
  if (!hasLocalStorage) {
    return false
  }

  while (Date.now() < deadline) {
    requireCurrentAdminSession(owner)
    if (acquireRefreshLock()) {
      return true
    }
    await new Promise(resolve => setTimeout(resolve, AUTH_WRITE_LOCK_POLL_MS))
  }

  requireCurrentAdminSession(owner)
  return acquireRefreshLock()
}

const waitForRefreshSignal = (
  since: number, sessionId: string | null,
): Promise<RefreshSignal | null> => {
  if (typeof window === 'undefined') {
    return Promise.resolve(null)
  }

  const belongsToSession = (signal: RefreshSignal): boolean =>
    signal.sessionId === sessionId
    // An older tab's success is usable only through the validated storage read below.
    || (signal.sessionId === undefined && signal.type === 'success')

  const existing = readRefreshSignal()
  if (existing && belongsToSession(existing) && existing.type !== 'start' && existing.at >= since) {
    return Promise.resolve(existing)
  }

  return new Promise((resolve) => {
    let settled = false
    const timeoutId = window.setTimeout(() => {
      if (settled) return
      settled = true
      cleanup()
      resolve(null)
    }, REFRESH_LOCK_TTL_MS)

    const handleSignal = (signal: RefreshSignal | null) => {
      if (!signal || !belongsToSession(signal) || signal.at < since || settled) {
        return
      }
      latestRefreshSignal = signal
      if (signal.type === 'start') {
        return
      }
      settled = true
      cleanup()
      resolve(signal)
    }

    const handleMessage = (event: MessageEvent) => {
      handleSignal(parseRefreshSignal(event.data))
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== REFRESH_SIGNAL_KEY || !event.newValue) {
        return
      }
      try {
        handleSignal(parseRefreshSignal(JSON.parse(event.newValue)))
      } catch {
        // Ignore invalid payload
      }
    }

    const cleanup = () => {
      window.clearTimeout(timeoutId)
      if (adminRefreshChannel) {
        adminRefreshChannel.removeEventListener('message', handleMessage)
      }
      window.removeEventListener('storage', handleStorage)
    }

    if (adminRefreshChannel) {
      adminRefreshChannel.addEventListener('message', handleMessage)
    }
    window.addEventListener('storage', handleStorage)
  })
}

if (adminRefreshChannel) {
  adminRefreshChannel.addEventListener('message', (event) => {
    const signal = parseRefreshSignal(event.data)
    if (signal) {
      latestRefreshSignal = signal
    }
  })
}

const refreshClient = axios.create({
  baseURL: apiBaseUrl,
  timeout: REFRESH_TIMEOUT_MS,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-App-Client': getApiClientHeader(),
    'X-Refresh-Token-Mode': 'cookie',
  },
})

const getRefreshCredentials = () => {
  const sessionId = localStorage.getItem('admin_portal_session_id')
  return { sessionId }
}

const shouldSkipRefresh = (url?: string) => {
  if (!url) return false
  const isRestrictedMfaEnrollment =
    (url.includes('/admin/auth/mfa/setup') ||
      url.includes('/admin/auth/mfa/confirm')) &&
    !localStorage.getItem('admin_portal_session_id')
  return (
    url.includes('/admin/auth/login') ||
    url.includes('/admin/auth/mfa/recover-login') ||
    url.includes('/admin/auth/mfa/confirm') ||
    isRestrictedMfaEnrollment ||
    url.includes('/admin/auth/refresh') ||
    url.includes('/admin/auth/logout')
  )
}

const isRefreshRequest = (url?: string): boolean =>
  Boolean(url && url.includes('/admin/auth/refresh'))

const isUnauthorizedError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 401

const readRefreshedAdminSession = (): RefreshResponse | null => {
  if (typeof window === 'undefined' || !hasLocalStorage) {
    return null
  }
  const token = localStorage.getItem('admin_portal_token')
  const expiresAt = localStorage.getItem('admin_portal_expires_at')
  const sessionId = localStorage.getItem('admin_portal_session_id')
  if (!isAdminTokenValid(token) || !expiresAt || !sessionId) {
    return null
  }
  return { token, expiresAt, sessionId }
}

let refreshPromise: Promise<RefreshResponse> | null = null
let refreshOwner: AdminSessionOwner | null = null
let authWritePending = false

const waitForRefreshCompletion = async (
  owner: AdminSessionOwner, pending: Promise<RefreshResponse>, deadline: number,
): Promise<void> => {
  requireCurrentAdminSession(owner)
  const remaining = Math.max(0, deadline - Date.now())
  let timeout: ReturnType<typeof setTimeout> | undefined
  const timeoutPromise = new Promise<boolean>(resolve => {
    timeout = setTimeout(() => resolve(false), remaining)
  })
  const settled = await Promise.race([pending.then(() => true, () => true), timeoutPromise])
  if (timeout !== undefined) clearTimeout(timeout)
  requireCurrentAdminSession(owner)
  if (!settled) throw new AdminAuthBusyError()
}

/**
 * Cookie-writing authentication requests must not overtake a refresh in this tab.
 * Login waits once for an in-flight refresh before sending credentials; MFA
 * confirmation remains fail-fast and no submitted TOTP is replayed automatically.
 */
export async function runAdminAuthWrite<T>(
  owner: AdminSessionOwner, request: () => Promise<T>,
  accept?: (result: T) => void,
  options: { waitForRefreshLock?: boolean } = {},
): Promise<T> {
  requireCurrentAdminSession(owner)
  if (authWritePending) throw new AdminAuthBusyError()
  authWritePending = true
  let ownsRefreshLock = false
  try {
    if (refreshPromise) {
      if (options.waitForRefreshLock !== true || owner.sessionId) {
        throw new AdminAuthBusyError()
      }
      const deadline = Date.now() + AUTH_WRITE_LOCK_WAIT_MS
      await waitForRefreshCompletion(owner, refreshPromise, deadline)
      requireCurrentAdminSession(owner)
      ownsRefreshLock = acquireRefreshLock()
        || await waitForRefreshLock(owner, deadline)
    } else {
      ownsRefreshLock = acquireRefreshLock()
        || (options.waitForRefreshLock === true && await waitForRefreshLock(owner))
    }
    if (!ownsRefreshLock) throw new AdminAuthBusyError()
    const result = await request()
    requireCurrentAdminSession(owner)
    // Commit credentials or retire the prior MFA session before releasing the lease.
    accept?.(result)
    return result
  } catch (error) {
    if (owner.sessionId && isCurrentAdminSession(owner) && isUnauthorizedError(error)) {
      // An authenticated MFA write cannot refresh while it owns the cookie lock.
      // Release it first and refresh independently; never replay the submitted OTP.
      queueMicrotask(() => {
        if (!isCurrentAdminSession(owner)) return
        void queueAdminRefresh().catch(refreshError => {
          if (isCurrentAdminSession(owner) && isUnauthorizedError(refreshError)) {
            clearAdminSession(owner)
            window.location.href = '/login'
          }
        })
      })
      throw new AdminAuthBusyError()
    }
    throw error
  } finally {
    authWritePending = false
    if (ownsRefreshLock) releaseRefreshLock()
  }
}

export const queueAdminRefresh = (): Promise<RefreshResponse> => {
  if (authWritePending) return Promise.reject(new AdminAuthBusyError())
  const owner = captureAdminSession()
  if (refreshPromise && refreshOwner && isCurrentAdminSession(refreshOwner)) return refreshPromise
  const previous = refreshPromise
  const next = (async () => {
    // A replacement session must not overlap the previous cookie-writing request.
    if (previous) await previous.catch(() => undefined)
    requireCurrentAdminSession(owner)
    for (let attempt = 0; ; attempt += 1) {
      try { return await refreshAdminSessionWithLock(owner) } catch (error) {
        requireCurrentAdminSession(owner)
        const transient = axios.isAxiosError(error)
          && (!error.response || error.response.status >= 500)
        if (!transient || attempt >= 2) throw error
        await waitForAdminRefreshRetry(owner, 1000 * (2 ** attempt))
        requireCurrentAdminSession(owner)
      }
    }
  })()
  refreshOwner = owner
  refreshPromise = next
  void next.finally(() => {
    if (refreshPromise === next) { refreshPromise = null; refreshOwner = null }
  }).catch(() => undefined)
  return next
}

const refreshAdminSessionWithLock = async (owner: AdminSessionOwner): Promise<RefreshResponse> => {
  requireCurrentAdminSession(owner)
  const currentToken = hasLocalStorage ? localStorage.getItem('admin_portal_token') : null

  if (!hasLocalStorage) {
    const latestSignal = readRefreshSignal()
    if (
      latestSignal?.type === 'start' &&
      latestSignal.owner !== ADMIN_TAB_ID &&
      Date.now() - latestSignal.at < REFRESH_LOCK_TTL_MS
    ) {
      const signal = await waitForRefreshSignal(latestSignal.at, owner.sessionId)
      requireCurrentAdminSession(owner)
      if (signal?.type === 'success') {
        const refreshed = readRefreshedAdminSession()
        if (refreshed && (!currentToken || refreshed.token !== currentToken)) {
          return refreshed
        }
      }
      if (signal?.type === 'unauthorized' && signal.sessionId === owner.sessionId) {
        throw buildUnauthorizedRefreshError()
      }
      if (signal?.type === 'failed') {
        throw new Error('Token refresh failed')
      }
    }

    broadcastRefreshSignal({
      type: 'start',
      sessionId: owner.sessionId,
      owner: ADMIN_TAB_ID,
      at: Date.now(),
    })
    try {
      const response = await refreshAdminSession(owner)
      requireCurrentAdminSession(owner)
      broadcastRefreshSignal({
        type: 'success',
        sessionId: owner.sessionId,
        owner: ADMIN_TAB_ID,
        at: Date.now(),
      })
      return response
    } catch (error) {
      requireCurrentAdminSession(owner)
      broadcastRefreshSignal({
        type: isUnauthorizedError(error) ? 'unauthorized' : 'failed',
        sessionId: owner.sessionId,
        owner: ADMIN_TAB_ID,
        at: Date.now(),
      })
      throw error
    }
  }

  if (acquireRefreshLock()) {
    try {
      const response = await refreshAdminSession(owner)
      requireCurrentAdminSession(owner)
      broadcastRefreshSignal({
        type: 'success',
        sessionId: owner.sessionId,
        owner: ADMIN_TAB_ID,
        at: Date.now(),
      })
      return response
    } catch (error) {
      requireCurrentAdminSession(owner)
      broadcastRefreshSignal({
        type: isUnauthorizedError(error) ? 'unauthorized' : 'failed',
        sessionId: owner.sessionId,
        owner: ADMIN_TAB_ID,
        at: Date.now(),
      })
      throw error
    } finally {
      releaseRefreshLock()
    }
  }

  const signal = await waitForRefreshSignal(Date.now(), owner.sessionId)
  requireCurrentAdminSession(owner)
  if (signal?.type === 'success') {
    const refreshed = readRefreshedAdminSession()
    if (refreshed && (!currentToken || refreshed.token !== currentToken)) {
      return refreshed
    }
  }

  if (signal?.type === 'unauthorized' && signal.sessionId === owner.sessionId) {
    throw buildUnauthorizedRefreshError()
  }

  if (signal?.type === 'failed') {
    throw new Error('Token refresh failed')
  }

  if (acquireRefreshLock()) {
    try {
      const response = await refreshAdminSession(owner)
      requireCurrentAdminSession(owner)
      broadcastRefreshSignal({
        type: 'success',
        sessionId: owner.sessionId,
        owner: ADMIN_TAB_ID,
        at: Date.now(),
      })
      return response
    } catch (error) {
      requireCurrentAdminSession(owner)
      broadcastRefreshSignal({
        type: isUnauthorizedError(error) ? 'unauthorized' : 'failed',
        sessionId: owner.sessionId,
        owner: ADMIN_TAB_ID,
        at: Date.now(),
      })
      throw error
    } finally {
      releaseRefreshLock()
    }
  }

  throw new Error('Token refresh timed out')
}

export const refreshAdminSession = async (owner = captureAdminSession()): Promise<RefreshResponse> => {
  requireCurrentAdminSession(owner)
  const { sessionId } = getRefreshCredentials()
  if (!sessionId) {
    throw new Error('Missing refresh credentials')
  }

  const response = await refreshClient.post<{
    success: boolean
    data?: RefreshResponse
    error?: string
    message?: string
  }>(
    '/admin/auth/refresh',
    {},
    {
      headers: {
        'X-Session-Id': sessionId,
        'X-App-Client': getApiClientHeader(),
      },
    }
  )

  if (!response.data?.success || !response.data.data) {
    throw new Error(response.data?.error || response.data?.message || 'Token refresh failed')
  }

  commitAdminRefresh(response.data.data, owner)
  return response.data.data
}

// Create axios instance for regular API calls
export const apiClient = axios.create({
  baseURL: apiBaseUrl,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Refresh-Token-Mode': 'cookie',
  },
})

// Create separate axios instance for admin API calls
export const adminApiClient = axios.create({
  baseURL: apiBaseUrl,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Refresh-Token-Mode': 'cookie',
  },
})

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const owned = config as AxiosRequestConfigWithRetry
    owned.__adminSessionOwner ??= captureAdminSession()
    requireCurrentAdminSession(owned.__adminSessionOwner)
    const token = localStorage.getItem('admin_portal_token')
    owned.__adminAccessToken = token
    config.headers = config.headers ?? {}
    const preserveAppClientHeader = (
      config as AxiosRequestConfigWithRetry
    ).__preserveAppClientHeader === true
    const explicitAppClientHeader = resolveExplicitAppClientHeader(config.headers)
    if (!preserveAppClientHeader && !explicitAppClientHeader) {
      config.headers['X-App-Client'] = getApiClientHeader()
    }
    config.headers['X-Refresh-Token-Mode'] = 'cookie'
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    } else {
      delete config.headers.Authorization
    }
    if (import.meta.env.DEV) {
      console.debug('[apiClient] request', config.method, config.url, { baseURL: config.baseURL })
    }
    return config
  },
  (error) => {
    if (import.meta.env.DEV) {
      console.error(
        '[apiClient] request error',
        sanitizeErrorForLogging(error)
      )
    }
    throw error
  },
  { synchronous: true }
)

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const owner = (response.config as AxiosRequestConfigWithRetry).__adminSessionOwner
    if (owner) requireCurrentAdminSession(owner)
    return response
  },
  async (error) => {
    if (import.meta.env.DEV) {
      console.error('[apiClient] response error', {
        url: error?.config?.url,
        status: error?.response?.status,
        data: error?.response?.data,
      })
    }

    const originalRequest = error.config as AxiosRequestConfigWithRetry | undefined
    if (originalRequest?.__adminSessionOwner) {
      requireCurrentAdminSession(originalRequest.__adminSessionOwner)
    }
    if (error.response?.status !== 401 || !originalRequest) {
      return Promise.reject(error)
    }

    if (shouldSkipRefresh(originalRequest.url) || originalRequest._retry) {
      if (isRefreshRequest(originalRequest.url) && isUnauthorizedError(error)) {
        clearAdminSession(originalRequest.__adminSessionOwner)
        window.location.href = '/login'
      }
      return Promise.reject(error)
    }

    const { sessionId } = getRefreshCredentials()
    if (!sessionId) {
      return Promise.reject(error)
    }

    try {
      originalRequest._retry = true
      const latestToken = localStorage.getItem('admin_portal_token')
      if (isAdminTokenValid(latestToken) && latestToken !== originalRequest.__adminAccessToken) {
        return apiClient(originalRequest)
      }
      const refreshed = await queueAdminRefresh()
      originalRequest.headers = originalRequest.headers ?? {}
      originalRequest.headers.Authorization = `Bearer ${refreshed.token}`
      return apiClient(originalRequest)
    } catch (refreshError) {
      if (originalRequest.__adminSessionOwner) {
        requireCurrentAdminSession(originalRequest.__adminSessionOwner)
      }
      if (isUnauthorizedError(refreshError)) {
        clearAdminSession(originalRequest.__adminSessionOwner)
        window.location.href = '/login'
      }
      return Promise.reject(refreshError)
    }
  }
)

// Add the same interceptors to admin API client
adminApiClient.interceptors.request.use(
  (config) => {
    const owned = config as AxiosRequestConfigWithRetry
    owned.__adminSessionOwner ??= captureAdminSession()
    requireCurrentAdminSession(owned.__adminSessionOwner)
    const token = localStorage.getItem('admin_portal_token')
    owned.__adminAccessToken = token
    config.headers = config.headers ?? {}
    const preserveAppClientHeader = (
      config as AxiosRequestConfigWithRetry
    ).__preserveAppClientHeader === true
    const explicitAppClientHeader = resolveExplicitAppClientHeader(config.headers)
    if (!preserveAppClientHeader && !explicitAppClientHeader) {
      config.headers['X-App-Client'] = getApiClientHeader()
    }
    config.headers['X-Refresh-Token-Mode'] = 'cookie'
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    } else {
      delete config.headers.Authorization
    }
    if (import.meta.env.DEV) {
      console.debug('[adminApiClient] request', config.method, config.url, { baseURL: config.baseURL })
    }
    return config
  },
  (error) => {
    if (import.meta.env.DEV) {
      console.error(
        '[adminApiClient] request error',
        sanitizeErrorForLogging(error)
      )
    }
    throw error
  },
  { synchronous: true }
)

adminApiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const owner = (response.config as AxiosRequestConfigWithRetry).__adminSessionOwner
    if (owner) requireCurrentAdminSession(owner)
    return response
  },
  async (error) => {
    if (import.meta.env.DEV) {
      console.error('[adminApiClient] response error', {
        url: error?.config?.url,
        status: error?.response?.status,
        data: error?.response?.data,
      })
    }
    const originalRequest = error.config as AxiosRequestConfigWithRetry | undefined
    if (originalRequest?.__adminSessionOwner) {
      requireCurrentAdminSession(originalRequest.__adminSessionOwner)
    }
    if (error.response?.status !== 401 || !originalRequest) {
      return Promise.reject(error)
    }

    if (shouldSkipRefresh(originalRequest.url) || originalRequest._retry) {
      if (isRefreshRequest(originalRequest.url) && isUnauthorizedError(error)) {
        clearAdminSession(originalRequest.__adminSessionOwner)
        window.location.href = '/login'
      }
      return Promise.reject(error)
    }

    const { sessionId } = getRefreshCredentials()
    if (!sessionId) {
      return Promise.reject(error)
    }

    try {
      originalRequest._retry = true
      const latestToken = localStorage.getItem('admin_portal_token')
      if (isAdminTokenValid(latestToken) && latestToken !== originalRequest.__adminAccessToken) {
        return adminApiClient(originalRequest)
      }
      const refreshed = await queueAdminRefresh()
      originalRequest.headers = originalRequest.headers ?? {}
      originalRequest.headers.Authorization = `Bearer ${refreshed.token}`
      return adminApiClient(originalRequest)
    } catch (refreshError) {
      if (originalRequest.__adminSessionOwner) {
        requireCurrentAdminSession(originalRequest.__adminSessionOwner)
      }
      if (isUnauthorizedError(refreshError)) {
        clearAdminSession(originalRequest.__adminSessionOwner)
        window.location.href = '/login'
      }
      return Promise.reject(refreshError)
    }
  }
)

export default apiClient
